import {
  forwardRef,
  ImgHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import { shimmerTranslateX } from "../styles/animations";

export type ImagePlaceholderTone = "light" | "dark" | "stamp";

interface GracefulImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  staggerIndex?: number;
  staggerMs?: number;
  maxStaggerMs?: number;
  eager?: boolean;
  loadWhen?: boolean;
  rootMargin?: string;
  wrapperClassName?: string;
  imgClassName?: string;
  aspectRatio?: string;
  placeholderTone?: ImagePlaceholderTone;
}

const placeholderStyles: Record<
  ImagePlaceholderTone,
  { base: string; shimmer: string }
> = {
  light: {
    base: "#eceaea",
    shimmer: "rgba(255, 255, 255, 0.55)",
  },
  dark: {
    base: "#1b1722",
    shimmer: "rgba(255, 0, 102, 0.14)",
  },
  stamp: {
    base: "#e8e4e4",
    shimmer: "rgba(255, 0, 102, 0.1)",
  },
};

const ImageWrap = styled.span<{
  $loaded: boolean;
  $fill?: boolean;
  $aspectRatio?: string;
  $tone: ImagePlaceholderTone;
}>`
  display: ${({ $fill, $aspectRatio }) =>
    $fill || $aspectRatio ? "block" : "inline-block"};
  width: ${({ $fill }) => ($fill ? "100%" : "auto")};
  height: ${({ $fill }) => ($fill ? "100%" : "auto")};
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || "auto"};
  position: relative;
  overflow: hidden;
  background: ${({ $tone }) => placeholderStyles[$tone].base};

  img {
    position: relative;
    z-index: 1;
    opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
    transition: opacity 0.35s ease;
  }
`;

const Placeholder = styled.span<{
  $loaded: boolean;
  $tone: ImagePlaceholderTone;
}>`
  position: absolute;
  inset: 0;
  z-index: 0;
  background: ${({ $tone }) => placeholderStyles[$tone].base};
  opacity: ${({ $loaded }) => ($loaded ? 0 : 1)};
  transition: opacity 0.35s ease;
  pointer-events: none;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${({ $tone }) => placeholderStyles[$tone].shimmer} 50%,
      transparent 100%
    );
    animation: ${shimmerTranslateX} 1.35s ease-in-out infinite;
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  ${({ $loaded }) =>
    $loaded &&
    css`
      animation: none;
      &::after {
        display: none;
      }
    `}
`;

const GracefulImage = forwardRef<HTMLImageElement, GracefulImageProps>(
  (
    {
      src,
      alt = "",
      staggerIndex = 0,
      staggerMs = 0,
      maxStaggerMs = 400,
      eager = false,
      loadWhen,
      rootMargin = "320px 0px",
      className,
      wrapperClassName,
      imgClassName,
      aspectRatio,
      placeholderTone = "light",
      style,
      onLoad,
      onError,
      ...rest
    },
    ref
  ) => {
    const wrapRef = useRef<HTMLSpanElement>(null);
    const imgRef = useRef<HTMLImageElement | null>(null);
    const [shouldLoad, setShouldLoad] = useState(
      eager || loadWhen === true
    );
    const [loaded, setLoaded] = useState(false);

    const setRefs = useCallback(
      (node: HTMLImageElement | null) => {
        imgRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) {
          (ref as React.MutableRefObject<HTMLImageElement | null>).current =
            node;
        }
      },
      [ref]
    );

    const markLoaded = () => setLoaded(true);

    useEffect(() => {
      if (!shouldLoad || !src) return;

      const img = imgRef.current;
      if (img?.complete && img.naturalWidth > 0) {
        markLoaded();
      }
    }, [shouldLoad, src]);

    useEffect(() => {
      if (eager) setShouldLoad(true);
    }, [eager]);

    useEffect(() => {
      if (eager) return;

      if (loadWhen === true) {
        const delay = Math.min(staggerIndex * staggerMs, maxStaggerMs);
        const timeoutId = window.setTimeout(() => setShouldLoad(true), delay);
        return () => window.clearTimeout(timeoutId);
      }

      if (loadWhen === false) {
        setShouldLoad(false);
        setLoaded(false);
      }
    }, [loadWhen, eager, staggerIndex, staggerMs, maxStaggerMs]);

    useEffect(() => {
      if (loadWhen !== undefined || eager || shouldLoad) return;

      const element = wrapRef.current;
      if (!element) return;

      const delay = Math.min(staggerIndex * staggerMs, maxStaggerMs);
      let timeoutId: number | null = null;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;

          timeoutId = window.setTimeout(() => setShouldLoad(true), delay);
          observer.disconnect();
        },
        { root: null, rootMargin, threshold: 0 }
      );

      observer.observe(element);

      return () => {
        observer.disconnect();
        if (timeoutId) window.clearTimeout(timeoutId);
      };
    }, [
      loadWhen,
      eager,
      shouldLoad,
      staggerIndex,
      staggerMs,
      maxStaggerMs,
      rootMargin,
    ]);

    const fill = style?.width === "100%" && style?.height === "100%";

    return (
      <ImageWrap
        ref={wrapRef}
        className={wrapperClassName ?? className}
        $loaded={loaded}
        $fill={fill}
        $aspectRatio={aspectRatio}
        $tone={placeholderTone}
      >
        <Placeholder $loaded={loaded} $tone={placeholderTone} aria-hidden />
        {shouldLoad && src ? (
          <img
            ref={setRefs}
            src={src}
            alt={alt}
            className={imgClassName}
            style={
              aspectRatio
                ? { ...style, width: "100%", height: "100%" }
                : style
            }
            decoding="async"
            onLoad={(event) => {
              markLoaded();
              onLoad?.(event);
            }}
            onError={(event) => {
              // Stop the shimmer so a failed load doesn't animate forever.
              markLoaded();
              onError?.(event);
            }}
            {...rest}
          />
        ) : null}
      </ImageWrap>
    );
  }
);

GracefulImage.displayName = "GracefulImage";

export default GracefulImage;
